import { useCallback } from 'react';

import { css, styled } from '@mui/material';
import { Switch, Typography } from '@kc-console/common-ui';
import {
  requestWebNotificationPermission,
  useNotificationPreferenceStore,
} from '@kc-console/context';

import type { ChangeEvent } from 'react';
import type { NotificationPreferenceType } from '@kc-console/context';

interface NotificationPreferenceToggleProps {
  description: string;
  title: string;
  checked?: boolean;
  disabled?: boolean;
  type?: NotificationPreferenceType;
}

const NotificationPreferenceToggle = ({
  checked: checkedProp,
  description,
  disabled = false,
  title,
  type,
}: NotificationPreferenceToggleProps) => {
  const { browserPushEnabled, inAppPushEnabled, updatePreference } =
    useNotificationPreferenceStore();

  const checked =
    checkedProp ??
    (type === 'browserPush' ? browserPushEnabled : type === 'inAppPush' ? inAppPushEnabled : false);

  const handleChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      if (!type) {
        return;
      }

      const enabled = event.target.checked;

      if (type === 'browserPush' && enabled) {
        const permission = await requestWebNotificationPermission();

        updatePreference(type, permission === 'granted');
        return;
      }

      updatePreference(type, enabled);
    },
    [type, updatePreference],
  );

  return (
    <PreferenceItem $disabled={disabled}>
      <PreferenceText>
        <Typography variant="label/regular/md" color={disabled ? 'text/disabled' : undefined}>
          {title}
        </Typography>
        <Typography
          variant="label/regular/xs"
          color={disabled ? 'text/disabled' : 'text/weakest/default'}
        >
          {description}
        </Typography>
      </PreferenceText>
      <Switch size="sm" checked={checked} disabled={disabled} onChange={handleChange} />
    </PreferenceItem>
  );
};

export default NotificationPreferenceToggle;

const PreferenceItem = styled('div')<{ $disabled: boolean }>(
  ({ $disabled }) => css`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    padding: 6px 16px;
    opacity: ${$disabled ? 0.3 : 1};
  `,
);

const PreferenceText = styled('div')`
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-width: 0;
`;
